import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateRecurringPayment, useUpdateRecurringPayment } from '@/hooks/finance/useRecurringPayments';
import { RecurringPayment, CreateRecurringPaymentInput } from '@/types/database/recurring-payments';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAllProfiles } from '@/hooks/profile/use-all-profiles';


interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: RecurringPayment | null;
}

export default function NewRecurringPaymentDialog({ open, onOpenChange, payment }: Props) {
  const createMutation = useCreateRecurringPayment();
  const updateMutation = useUpdateRecurringPayment();
  const isEditing = !!payment;
  const { data: profiles } = useAllProfiles();
  const [assignMode, setAssignMode] = useState<'user' | 'custom'>('user');

  const { data: categories } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const { data } = await supabase.from('expense_categories').select('id, name, code').eq('is_active', true).order('name');
      return data || [];
    },
  });

  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const { data } = await supabase.from('departments').select('id, name, code').eq('is_active', true).order('name');
      return data || [];
    },
  });

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<CreateRecurringPaymentInput>();

  useEffect(() => {
    if (open) {
      if (payment) {
        reset({
          name: payment.name,
          description: payment.description || '',
          vendor_name: payment.vendor_name || '',
          amount: payment.amount,
          currency: payment.currency || 'ETB',
          frequency: payment.frequency as any,
          next_due_date: payment.next_due_date,
          start_date: payment.start_date,
          end_date: payment.end_date || '',
          category_id: payment.category_id || '',
          department_id: payment.department_id || '',
          payment_type: payment.payment_type || 'other',
          assigned_to: payment.assigned_to || '',
          assigned_name: payment.assigned_name || '',
        });
        // Determine mode: if assigned_to matches a profile, use 'user' mode
        if (payment.assigned_to && profiles?.some(p => p.id === payment.assigned_to)) {
          setAssignMode('user');
        } else if (payment.assigned_name) {
          setAssignMode('custom');
        } else {
          setAssignMode('user');
        }
      } else {
        reset({
          name: '',
          description: '',
          vendor_name: '',
          amount: 0,
          currency: 'ETB',
          frequency: 'monthly',
          next_due_date: '',
          start_date: new Date().toISOString().split('T')[0],
          end_date: '',
          category_id: '',
          department_id: '',
          payment_type: 'other',
          assigned_to: '',
          assigned_name: '',
        });
      }
    }
  }, [open, payment, reset]);

  const onSubmit = (data: CreateRecurringPaymentInput) => {
    // Clean empty strings to undefined
    const cleaned = {
      ...data,
      description: data.description || undefined,
      vendor_name: data.vendor_name || undefined,
      end_date: data.end_date || undefined,
      category_id: data.category_id || undefined,
      department_id: data.department_id || undefined,
      assigned_to: data.assigned_to || undefined,
      assigned_name: data.assigned_name || undefined,
    };

    if (isEditing) {
      updateMutation.mutate({ id: payment.id, ...cleaned }, { onSuccess: () => onOpenChange(false) });
    } else {
      createMutation.mutate(cleaned, { onSuccess: () => onOpenChange(false) });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Recurring Payment' : 'Add Recurring Payment'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" {...register('name', { required: true })} placeholder="e.g. Office Rent" />
            </div>

            <div>
              <Label htmlFor="amount">Amount *</Label>
              <Input id="amount" type="number" step="0.01" {...register('amount', { required: true, valueAsNumber: true })} />
            </div>

            <div>
              <Label htmlFor="currency">Currency</Label>
              <Select value={watch('currency') || 'ETB'} onValueChange={v => setValue('currency', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ETB">ETB</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="frequency">Frequency</Label>
              <Select value={watch('frequency') || 'monthly'} onValueChange={v => setValue('frequency', v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="payment_type">Payment Type</Label>
              <Select value={watch('payment_type') || 'other'} onValueChange={v => setValue('payment_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rent">Rent</SelectItem>
                  <SelectItem value="lease">Lease</SelectItem>
                  <SelectItem value="loan">Loan</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="utility">Utility</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="next_due_date">Next Due Date *</Label>
              <Input id="next_due_date" type="date" {...register('next_due_date', { required: true })} />
            </div>

            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input id="start_date" type="date" {...register('start_date')} />
            </div>

            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input id="end_date" type="date" {...register('end_date')} />
            </div>

            <div>
              <Label htmlFor="vendor_name">Vendor</Label>
              <Input id="vendor_name" {...register('vendor_name')} placeholder="e.g. Landlord" />
            </div>

            <div>
              <Label htmlFor="category_id">Category</Label>
              <Select value={watch('category_id') || ''} onValueChange={v => setValue('category_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories?.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="department_id">Department</Label>
              <Select value={watch('department_id') || ''} onValueChange={v => setValue('department_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {departments?.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label>Assigned To</Label>
              <div className="flex gap-2 mb-1">
                <Button type="button" size="sm" variant={assignMode === 'user' ? 'default' : 'outline'} onClick={() => { setAssignMode('user'); setValue('assigned_name', ''); }}>
                  Select User
                </Button>
                <Button type="button" size="sm" variant={assignMode === 'custom' ? 'default' : 'outline'} onClick={() => { setAssignMode('custom'); setValue('assigned_to', ''); }}>
                  Custom Name
                </Button>
              </div>
              {assignMode === 'user' ? (
                <Select value={watch('assigned_to') || ''} onValueChange={v => {
                  setValue('assigned_to', v);
                  const profile = profiles?.find(p => p.id === v);
                  setValue('assigned_name', profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '');
                }}>
                  <SelectTrigger><SelectValue placeholder="Select a user" /></SelectTrigger>
                  <SelectContent>
                    {profiles?.filter(p => p.status === 'active').map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {`${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email || 'Unknown'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input {...register('assigned_name')} placeholder="Enter name manually" />
              )}
            </div>

            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" {...register('description')} rows={2} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
